import type { JsonSchema, UISchemaElement } from "@jsonforms/core";
import type { CollectionSpec } from "../constants/caseReviewSections";
import { groupLabelOf, recordDetailLayout } from "./caseReviewRecords";

export type CollectionPresentation = Pick<CollectionSpec, "labels" | "title" | "tags">;

/** Finds a collection's item schema from the authored section schema. */
export function catalogCollectionSchema(schema: JsonSchema, collectionId: string,
  childId?: string): JsonSchema | undefined {
  let node = schema.properties?.[collectionId] as JsonSchema | undefined;
  node = node?.type === "array" ? node.items as JsonSchema | undefined : undefined;
  for (const segment of childId?.split(".") ?? []) {
    node = node?.properties?.[segment] as JsonSchema | undefined;
    if (node?.type === "array") node = node.items as JsonSchema | undefined;
  }
  return node;
}

/** Data nesting and scopes come from the catalog. Only appearance may be overridden in UI code. */
export function catalogCollections(schema: JsonSchema, layout: UISchemaElement,
  presentation: Record<string, CollectionPresentation> = {}): CollectionSpec[] {
  const used = new Set<string>();
  const discover = (node: JsonSchema, ui: UISchemaElement, parent: string[] = []): CollectionSpec[] => {
    const result: CollectionSpec[] = [];
    for (const [name, property] of Object.entries(node.properties ?? {})) {
      const field = property as JsonSchema & { "x-collection-id"?: string; "x-field-id"?: string };
      const path = [...parent, name];
      if (field.type === "array" && field["x-collection-id"]) {
        const id = field["x-collection-id"];
        const scope = "#" + path.map((part) => "/properties/" + part.replace(/~/g, "~0").replace(/\//g, "~1")).join("");
        const detail = recordDetailLayout(ui, scope);
        if (!detail) throw new Error(`Catalog collection ${id} has no record layout (${scope}).`);
        used.add(id);
        const label = groupLabelOf(ui, scope) ?? field.title ?? name.replace(/_/g, " ");
        const visual = presentation[id] ?? {
          labels: { records: label, empty: "No records recorded.", add: "Add record", remove: "Remove record", item: label },
          title: () => undefined,
        };
        result.push({ ...visual, collectionId: path.join("."), scope,
          children: discover(field.items as JsonSchema, detail) });
      } else if (field.type === "object" && !field["x-field-id"]) {
        result.push(...discover(field, ui, path));
      }
    }
    return result;
  };
  const collections = discover(schema, layout);
  for (const id of Object.keys(presentation)) {
    if (!used.has(id)) throw new Error(`UI presentation names an unknown catalog collection: ${id}`);
  }
  return collections;
}
